// Sentry initialization for React 18 with aggressive PII scrubbing.
// No-op if REACT_APP_SENTRY_DSN is not configured.

import * as Sentry from "@sentry/react";

const SENSITIVE_KEYS = new Set([
  "password", "passwd", "pwd", "secret",
  "token", "access_token", "refresh_token", "id_token",
  "jwt", "api_key", "apikey", "auth", "authorization",
  "session", "cookie", "set-cookie", "x-api-key",
  "csrf", "csrf_token", "x-csrf-token",
  "credit_card", "card_number", "cvv",
  "national_id", "passport", "ssn",
]);

const AUTH_URL_FRAGMENTS = [
  "/login", "/auth", "/token", "/signin", "/sign-in",
  "/register", "/password", "/2fa", "/two-factor",
];

function scrub(data) {
  if (data == null) return data;
  if (Array.isArray(data)) return data.map(scrub);
  if (typeof data === "object") {
    const out = {};
    for (const [k, v] of Object.entries(data)) {
      out[k] = SENSITIVE_KEYS.has(String(k).toLowerCase()) ? "[Filtered]" : scrub(v);
    }
    return out;
  }
  return data;
}

function scrubHeaders(headers) {
  if (!headers || typeof headers !== "object") return headers;
  const redact = new Set(["authorization", "cookie", "set-cookie", "x-api-key", "x-csrf-token"]);
  const out = {};
  for (const [k, v] of Object.entries(headers)) {
    out[k] = redact.has(String(k).toLowerCase()) ? "[Filtered]" : v;
  }
  return out;
}

function beforeSend(event) {
  // Drop client IP from user context.
  if (event.user && typeof event.user === "object") {
    if ("ip_address" in event.user) event.user.ip_address = "[Filtered]";
  }

  if (event.request && typeof event.request === "object") {
    const req = { ...event.request };
    if (req.headers) req.headers = scrubHeaders(req.headers);
    if (req.cookies) req.cookies = "[Filtered]";

    const url = String(req.url || "").toLowerCase();
    const isAuth = AUTH_URL_FRAGMENTS.some((f) => url.includes(f));

    if ("data" in req) req.data = isAuth ? "[Filtered]" : scrub(req.data);
    if ("body" in req) req.body = isAuth ? "[Filtered]" : scrub(req.body);
    if (isAuth && "query_string" in req) req.query_string = "[Filtered]";

    event.request = req;
  }

  if (event.extra) event.extra = scrub(event.extra);
  if (event.contexts) event.contexts = scrub(event.contexts);
  if (Array.isArray(event.breadcrumbs)) event.breadcrumbs = scrub(event.breadcrumbs);

  return event;
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return false;

  const tracesSampleRate = parseFloat(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.1",
  );

  // Jun 29, 2026 — MED-05 defense. If the Sentry ingest keeps
  // returning 403 (mismatched project id / expired DSN), stop
  // wasting bandwidth. We remember the last-known-bad DSN in
  // localStorage so future page loads skip Sentry init entirely
  // until the DSN changes.
  try {
    if (typeof localStorage !== "undefined"
        && localStorage.getItem("mesp_sentry_disabled_for_dsn") === dsn) {
      return false;
    }
  } catch { /* ignore */ }

  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: isNaN(tracesSampleRate) ? 0.1 : tracesSampleRate,
    sendDefaultPii: false,
    beforeSend,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
    release: process.env.REACT_APP_SENTRY_RELEASE,
    // Trip the "disabled for this DSN" flag on repeated auth failures
    // so we don't spam the (broken) ingest endpoint.
    transport: undefined,
  });
  // Detect fatal auth failures once and self-disable for this DSN.
  try {
    if (typeof window !== "undefined" && !window.__mesp_sentry_watchdog__) {
      window.__mesp_sentry_watchdog__ = true;
      let authFailures = 0;
      const origFetch = window.fetch;
      window.fetch = async (...args) => {
        const resp = await origFetch(...args);
        try {
          const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
          if (url.includes("ingest") && url.includes("sentry.io")
              && (resp.status === 401 || resp.status === 403)) {
            authFailures += 1;
            if (authFailures >= 3) {
              try { localStorage.setItem("mesp_sentry_disabled_for_dsn", dsn); } catch { /* ignore */ }
              console.warn("[mesp] Sentry ingest rejected 3× → disabling client for this DSN until it changes.");
            }
          }
        } catch { /* ignore */ }
        return resp;
      };
    }
  } catch { /* ignore */ }
  return true;
}
